import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import moment from 'moment'; // Certifique-se de ter o Moment.js instalado

export function fromUsToBrDate(dateStr: string): string {
  const parts = dateStr.split('-'); // Divide a string de data nos hífens
  return `${parts[2]}-${parts[1]}-${parts[0]}`; // Reorganiza e retorna a nova string de data
}

export function formatToLocalDateString(dateString) {
  const parts = dateString.split('-');
  // O mês é 0-indexado em JavaScript: 0 para Janeiro, 1 para Fevereiro, etc.
  // Portanto, precisamos subtrair 1 do mês.
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  return date.toLocaleDateString('pt-BR');
}


export function formatDateToDDMMYYYY(date) {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Janeiro é 0!
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}


export function calcularIdade(dataNascimentoInput: Date | number | moment.Moment): number {

  try {
    // Normaliza a entrada para um objeto moment.js
    let dataNascimento = moment(dataNascimentoInput);

    // Se a entrada já for um objeto moment.js e for válida, usa como está
    if (moment.isMoment(dataNascimentoInput) && dataNascimentoInput.isValid()) {
      dataNascimento = dataNascimentoInput;
    } else if (dataNascimentoInput instanceof Date) {
      // Converte objeto Date para moment.js
      dataNascimento = moment(dataNascimentoInput);
    } else if (typeof dataNascimentoInput === 'number') {
      // Converte timestamp para moment.js
      dataNascimento = moment(dataNascimentoInput);
    }

    const hoje = moment();
    let idade = hoje.year() - dataNascimento.year();
    const m = hoje.month() - dataNascimento.month();

    if (m < 0 || (m === 0 && hoje.date() < dataNascimento.date())) {
      idade--;
    }

    return idade;
  }
  catch (error) {
    return 0;
  }
}

export function calcularDias(diaInput: Date | number | moment.Moment): number {
  try {
    // Normaliza a entrada para um objeto moment.js
    let dia = moment(diaInput);

    // Se a entrada já for um objeto moment.js e for válida, usa como está
    if (moment.isMoment(diaInput) && diaInput.isValid()) {
      dia = diaInput;
    } else if (diaInput instanceof Date) {
      // Converte objeto Date para moment.js
      dia = moment(diaInput);
    } else if (typeof diaInput === 'number') {
      // Converte timestamp para moment.js
      dia = moment(diaInput);
    }

    const hoje = moment();
    const dias = hoje.diff(dia, 'days');

    return dias;
  }
  catch (error) {
    return 0;
  }
}

export function dateStringToTimestamp(dateStr: string): number {
  const parts = dateStr.split('-'); // Assumindo o formato DD/MM/YYYY
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Mês é 0-indexado em objetos Date
  const year = parseInt(parts[2], 10);
  const date = new Date(year, month, day);
  return date.getTime(); // Retorna o timestamp
}

export function parseDateFromString(dateStr: string): Date {
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Mês é 0-indexado
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  throw new Error('Formato de data inválido.');
}


// Função para verificar se a data é válida
function isValidDate(d: Date): boolean {
  return !isNaN(d.getTime());
}

// Função validadora
export function validDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Se estiver nulo, não há o que validar
    if (value === null || value === '') {
      return null;
    }

    if (moment.isMoment(value) && value.isValid()) {
      // Se o valor for um objeto Moment válido
      return null; // Data válida
    } else if (value instanceof Date && !isNaN(value.getTime())) {
      // Se o valor for um objeto Date válido
      return null; // Data válida
    } else if (typeof value === 'number') {
      // Se for um número (timestamp), cria uma data e verifica se é válida
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return null; // Data válida
      }
    }
    // Em todos os outros casos, retorna um erro de validação
    return { invalidDate: 'Data inválida.' }; // Data inválida
  };
}

export function dateNotFutureValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const today = moment().endOf('day'); // Considera o final do dia para a comparação

    if (moment.isMoment(value)) {
      // Para objetos Moment.js
      if (value.isValid() && value.isSameOrBefore(today)) {
        return null; // Data válida
      }
    } else if (value instanceof Date) {
      // Para objetos Date
      if (!isNaN(value.getTime()) && moment(value).isSameOrBefore(today)) {
        return null; // Data válida
      }
    } else if (typeof value === 'number') {
      // Para timestamps
      const dateFromTimestamp = moment(value);
      if (!isNaN(value) && dateFromTimestamp.isSameOrBefore(today)) {
        return null; // Data válida
      }
    }
    // Em todos os outros casos, retorna um erro de validação
    return { futureDate: 'A data não pode ser no futuro.' }; // Data inválida
  };
}

export function maxYearsAgoValidator(maxYears: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const today = moment();
    const minAllowedDate = today.subtract(maxYears, 'years');

    if (moment.isMoment(value)) {
      // Para objetos Moment.js
      if (value.isValid() && value.isSameOrAfter(minAllowedDate)) {
        return null; // Data dentro do limite
      }
    } else if (value instanceof Date) {
      // Para objetos Date
      if (!isNaN(value.getTime()) && moment(value).isSameOrAfter(minAllowedDate)) {
        return null; // Data dentro do limite
      }
    } else if (typeof value === 'number') {
      // Para timestamps
      const dateFromTimestamp = moment(value);
      if (!isNaN(value) && dateFromTimestamp.isSameOrAfter(minAllowedDate)) {
        return null; // Data dentro do limite
      }
    }
    // Em todos os outros casos, retorna um erro de validação
    return { tooOld: `A data não pode ser mais de ${maxYears} anos atrás.` };
  };
}

export function fromTimestampToDate(date: any): String {
  const dateObj = new Date(date);

  // Extrair o dia, mês e ano
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0'); // Mês é zero-indexado, então adicionamos 1
  const year = dateObj.getFullYear();

  // Formatar como dia/mês/ano
  const formattedDate = `${day}/${month}/${year}`;

  return formattedDate
}

export function fromTimestampToMonth(date: any): String {
  const dateObj = new Date(date);

  // Extrair o dia, mês e ano
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0'); // Mês é zero-indexado, então adicionamos 1
  const year = dateObj.getFullYear();

  // Formatar como dia/mês/ano
  const formattedDate = `${month}/${year}`;

  return formattedDate
}

export function safeParseFromDateToString(date: string): any {
  if(date === null || date === '') {
    return ''
  } else {
    return parseDateFromString(fromUsToBrDate(date))
  }
}